/*
 * Copyright (c) 2024.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Pavel Bobylev <pavel.bobylev1988@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {AbstractControl, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {IntervalValidatorContent, NumberValidatorContent} from 'compob2b-libs';

export class CustomValidators {
  public static INT_INTERVAL_PATTERN = /^-?\d+\.{3}-?\d+$/;
  public static FLOAT_INTERVAL_PATTERN = /^-?\d+(\.\d+)?\.{3}-?\d+(\.\d+)?$/;
  public static INT_NUMBER_PATTERN = /^-?\d+$/;
  public static FLOAT_NUMBER_PATTERN = /^-?\d+(\.\d+)?$/;

  public static intervalValueValidator(validator: IntervalValidatorContent): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string | null = control.value;
      if (value === null || value.trim() === '') {
        return null;
      }

      if (!value.match(CustomValidators.FLOAT_INTERVAL_PATTERN)) {
        return {pattern: true};
      }

      if (validator.isInteger && !value.match(CustomValidators.INT_INTERVAL_PATTERN)) {
        return {notInteger: true};
      }

      const [lowerStrBoundary, upperStrBoundary] = value.split('...');
      const lowerBoundary: number = parseFloat(lowerStrBoundary);
      const upperBoundary: number = parseFloat(upperStrBoundary);
      if (lowerBoundary > upperBoundary) {
        return {lowerGtUpperBoundary: true};
      }

      const error: any = {};
      let hasErrors = false;
      if (validator.lowerBoundary !== null) {
        if (validator.lowerBoundary.minValue !== null && validator.lowerBoundary.minValue > lowerBoundary) {
          error.lowerMin = {min: validator.lowerBoundary.minValue, actual: lowerBoundary};
          hasErrors = true;
        } else  if (validator.lowerBoundary.maxValue !== null && validator.lowerBoundary.maxValue < lowerBoundary) {
          error.lowerMax = {max: validator.lowerBoundary.maxValue, actual: lowerBoundary};
          hasErrors = true;
        }
      }

      if (validator.upperBoundary !== null) {
        if (validator.upperBoundary.minValue !== null && validator.upperBoundary.minValue > upperBoundary) {
          error.upperMin = {min: validator.upperBoundary.minValue, actual: upperBoundary};
          hasErrors = true;
        } else if (validator.upperBoundary.maxValue !== null && validator.upperBoundary.maxValue < upperBoundary) {
          error.upperMax = {max: validator.upperBoundary.maxValue, actual: upperBoundary};
          hasErrors = true;
        }
      }

      return hasErrors ? error : null;
    };
  }

  public static numberValueValidator(validator: NumberValidatorContent): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string | null = control.value;
      if (value === null || value.trim() === '') {
        return null;
      }

      if (!value.match(CustomValidators.FLOAT_NUMBER_PATTERN)) {
        return {pattern: true};
      }

      if (validator.isInteger && !value.match(CustomValidators.INT_NUMBER_PATTERN)) {
        return {notInteger: true};
      }

      if (validator.minValue !== null) {
        const minValueValidation = Validators.min(validator.minValue)(control);
        if (minValueValidation !== null) {
          return minValueValidation;
        }
      }

      if (validator.maxValue !== null ) {
        const maxValueValidation = Validators.max(validator.maxValue)(control);
        if (maxValueValidation !== null) {
          return maxValueValidation;
        }
      }

      return null;
    };
  }

  public static minMaxValueGroupValidator(minFieldName: string, maxFieldName: string, checkAnyForNotEmpty: boolean = false): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const minValue: number | null = control.get(minFieldName).value;
      const maxValue: number | null = control.get(maxFieldName).value;

      if (minValue !== null && maxValue !== null && maxValue < minValue) {
        return {incorrectMinMaxValues: true};
      }

      if (!checkAnyForNotEmpty) {
        return null;
      }

      if (minValue !== null || maxValue !== null) {
        return null;
      }

      return {minMaxValuesValidatorEmpty: true};
    };
  }

  public static urlValidator(control: AbstractControl): ValidationErrors | null {
      const value = control.value;
      if (value === null || value === undefined || value.trim().length === 0) {
        return null;
      }

      try {
        new URL(value);
      } catch (e) {
        return {url: true};
      }

      return null;
  }
}
